import React from 'react';
import { Check, X } from 'lucide-react';

interface PasswordRequirement {
  label: string;
  met: boolean;
}

export interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

const passwordRequirements = [
  {
    key: 'length',
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    key: 'uppercase',
    label: 'One uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    key: 'lowercase',
    label: 'One lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    key: 'number',
    label: 'One number',
    test: (password: string) => /[0-9]/.test(password),
  },
  {
    key: 'special',
    label: 'One special character',
    test: (password: string) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
  },
];

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className = '',
}) => {
  const requirements: PasswordRequirement[] = passwordRequirements.map((req) => ({
    label: req.label,
    met: req.test(password),
  }));

  const metCount = requirements.filter((req) => req.met).length;
  const strengthPercentage = (metCount / requirements.length) * 100;

  const getStrengthLabel = () => {
    if (metCount === 0) return { label: '', color: 'bg-gray-200' };
    if (metCount <= 1) return { label: 'Weak', color: 'bg-red-500' };
    if (metCount <= 3) return { label: 'Fair', color: 'bg-yellow-500' };
    if (metCount === 4) return { label: 'Good', color: 'bg-blue-500' };
    return { label: 'Strong', color: 'bg-green-500' };
  };

  const strength = getStrengthLabel();

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength Meter */}
      {password.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Password Strength</span>
            {strength.label && (
              <span className={`text-sm font-medium ${
                strength.color === 'bg-red-500' ? 'text-red-600' :
                strength.color === 'bg-yellow-500' ? 'text-yellow-600' :
                strength.color === 'bg-blue-500' ? 'text-blue-600' :
                strength.color === 'bg-green-500' ? 'text-green-600' :
                'text-gray-600'
              }`}>
                {strength.label}
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${strength.color}`}
              style={{ width: `${strengthPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Requirements Checklist */}
      <div className="space-y-2">
        <span className="text-sm font-medium text-gray-700">Requirements:</span>
        <div className="space-y-1">
          {requirements.map((requirement, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 text-sm"
            >
              <div className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                requirement.met
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-400'
              }`}>
                {requirement.met ? (
                  <Check className="w-3 h-3" />
                ) : (
                  <X className="w-3 h-3" />
                )}
              </div>
              <span className={
                requirement.met ? 'text-green-700' : 'text-gray-500'
              }>
                {requirement.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
